import sentry from '../../sentry';
import {
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  VStack,
  Button,
  Box,
  Link,
  Text,
  Flex,
  useToast,
  InputGroup,
  InputRightElement,
  useColorModeValue,
  Divider,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
  Link as RouterLink,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import { useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import CustomToast from '../../common/CustomToast';
import { CustomVisibilityIcon } from '../../theme/icons/CustomVisibilityIcon';
import { CustomVisibilityOffIcon } from '../../theme/icons/CustomVisibilityOffIcon';
import { AuthContext } from '../../context/AuthContext';
import {
  setToken as storeToken,
  setImpersonating,
  clearTokens as clearTokenStore,
} from '../../utils/tokenStore';
import { accountApi } from '../../store/redux/api/accountApi';
import { HttpContext } from '../../context/HttpContext';
import { reconcileConsent } from '../../cookieConsent/consentReconciliation';
import { useCookieConsent } from '../../cookieConsent';
import { usePlanToken } from '../../hooks/usePlanToken';
import { useOAuthPopup } from '../../hooks/useOAuthPopup';
import SsoOptions from '../../components/client/account/SsoOptions';
import { trackingSearch } from '../../utils/trackingParams';

function LoginPage() {
  const { setAuthState, setImpersonationAuthState, logout, parseJwt } =
    useContext(AuthContext);
  const { authAxios, publicAxios } = useContext(HttpContext);
  const { hideBanner } = useCookieConsent();
  const dispatch = useDispatch();
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [searchParams] = useSearchParams();
  let nextPathname = location?.state?.next?.pathname ?? '/';
  let nextSearch = location?.state?.next?.search ?? '';
  if (nextPathname === '/logout') {
    nextPathname = '/';
    nextSearch = '';
  }

  const impersonationUuid = searchParams.get('impersonationUuid');

  const showPasswordClickHandler = () => setShow(!show);

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required('Please enter your email')
      .email('Please enter a valid email address'),
    password: Yup.string().required('Please enter your password'),
  });
  const formOptions = {
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty, isValid },
    reset,
  } = useForm(formOptions);

  const { planToken } = usePlanToken();

  const handleError = useCallback(
    (onError) => {
      sentry.sendError(onError);

      toast({
        render: (props) => (
          <CustomToast
            status="error"
            title={
              onError?.response?.data?.error ||
              onError?.response?.data?.errors[0] ||
              'Something went wrong. Please try again later.'
            }
            description=""
            onClose={props.onClose}
            id={props.id}
          />
        ),
      });
    },
    [toast]
  );

  const getAccount = useCallback(
    async (token, impersonation = false) => {
      const exp = parseJwt(token).exp;
      storeToken(token, exp);

      if (impersonation) {
        setImpersonating(true);
      }

      try {
        const { data } = await authAxios.get(`accounts/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const account = data.account || null;
        const subscribed = data.subscribed;
        const billingEnabled = data.billing_enabled;
        const mfaEnabled = data.mfa_enabled;

        if (account) {
          impersonation
            ? setImpersonationAuthState({ token, account })
            : setAuthState({ token, account });

          if (!impersonation) {
            reconcileConsent(account, authAxios, hideBanner);
          }
        }
        reset();
        dispatch(accountApi.util.resetApiState());

        if (billingEnabled === true && !subscribed) {
          if (planToken) {
            navigate(`/subscription?planToken=${planToken}`, { replace: true });
          } else {
            navigate('/subscription', { replace: true });
          }
        } else if (nextPathname !== '/') {
          setTimeout(
            () => navigate(`${nextPathname}${nextSearch}`, { replace: true }),
            2
          );
        } else {
          if (
            mfaEnabled &&
            (account.otp_required || account.otp_method === null) &&
            impersonation === false
          ) {
            setTimeout(() => {
              navigate('/mfa', {
                replace: true,
                state: { next: { pathname: '/dashboard' } },
              });
            }, 100);
          } else {
            navigate('/dashboard', { replace: true });
          }
        }
      } catch (error) {
        console.error('Error fetching account data:', error);
        handleError(error);
      }
    },
    [
      authAxios,
      dispatch,
      setAuthState,
      setImpersonationAuthState,
      parseJwt,
      reset,
      planToken,
      navigate,
      location,
      handleError,
    ]
  );

  const impersonate = useCallback(
    async (uuid) => {
      try {
        const response = await authAxios.post('accounts/me/impersonate', {
          uuid,
        });
        const { token } = response.data;
        if (token) {
          getAccount(token, true);
          sessionStorage.setItem('impersonation', true);
          navigate('/dashboard');
        }
      } catch (onError) {
        handleError(onError);
      }
    },
    [authAxios, getAccount, navigate, handleError]
  );

  const hasTriggeredImpersonation = useRef(false);
  useEffect(() => {
    if (!impersonationUuid || impersonationUuid.trim().length === 0) {
      hasTriggeredImpersonation.current = false;
      return;
    }
    if (hasTriggeredImpersonation.current) return;
    hasTriggeredImpersonation.current = true;
    clearTokenStore();
    impersonate(impersonationUuid);
  }, [impersonationUuid, impersonate]);

  const onSubmit = async (values) => {
    try {
      const { email, password } = values;
      const response = await publicAxios.post(`accounts/sign_in`, {
        account: {
          email: email,
          password: password,
        },
      });

      const { token } = response.data;

      if (token) {
        getAccount(token);
      }
    } catch (onError) {
      handleError(onError);
    }
  };

  const { setOauthPopup } = useOAuthPopup({
    onSuccess: (token) => {
      getAccount(token);
    },
  });

  const modeIconColor = useColorModeValue('secondaryDark06', '#DDDDDD');
  const modeText = useColorModeValue('secondaryDark08', '#DDDDDD');

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} w="100%">
        <VStack spacing={6} alignItems="flex-start">
          <VStack alignItems="flex-start" mb={4}>
            <Text textStyle="heading2">Welcome Back</Text>
            <Text pl={2} textStyle="bodyMedium">
              Don't have an account?{' '}
              <Link
                href={`/signup${location.search}`}
                textDecoration="underline"
              >
                Sign up now
              </Link>
            </Text>
          </VStack>
          <FormControl isRequired isInvalid={errors.email}>
            <FormLabel textStyle="bodyMedium" color="#666666">
              E-mail address
            </FormLabel>
            <Input
              id="email"
              type="email"
              placeholder="Your email address"
              {...register('email')}
              bg="white"
              borderRadius="12px"
              border="1px solid #D0D5DD"
              mt={1}
            />
            <FormErrorMessage mt={2} ml={5}>
              {errors.email?.message || errors.email}
            </FormErrorMessage>
          </FormControl>
          <FormControl isRequired isInvalid={errors.password}>
            <FormLabel textStyle="bodyMedium" color="#666666">
              Password
            </FormLabel>
            <InputGroup>
              <Input
                id="password"
                type={show ? 'text' : 'password'}
                placeholder="Your password"
                {...register('password')}
                bg="white"
                borderRadius="12px"
                border="1px solid #D0D5DD"
                mt={1}
              />
              <InputRightElement>
                {show ? (
                  <CustomVisibilityOffIcon
                    onClick={showPasswordClickHandler}
                    boxSize="18px"
                    cursor="pointer"
                    color={modeIconColor}
                  />
                ) : (
                  <CustomVisibilityIcon
                    onClick={showPasswordClickHandler}
                    boxSize="18px"
                    cursor="pointer"
                    color={modeIconColor}
                  />
                )}
              </InputRightElement>
            </InputGroup>
            <FormErrorMessage mt={2} ml={5}>
              {errors.password?.message || errors.password}
            </FormErrorMessage>
          </FormControl>
          <Flex w="full" justifyContent="flex-end">
            <Link
              as={RouterLink}
              to={`/forgot-password${trackingSearch()}`}
              color="#666666"
              textStyle="bodyMedium"
              fontSize="14px"
              textDecoration="underline"
            >
              Forgot password?
            </Link>
          </Flex>
          <Box w="full">
            <Button
              w="full"
              type="submit"
              isLoading={isSubmitting}
              disabled={!isDirty || !isValid || isSubmitting}
              alt="Login"
            >
              Sign in
            </Button>
          </Box>
          <Flex align="center" w="full" my={4}>
            <Divider borderColor="#D0D5DD" />
            <Text
              px={4}
              textStyle="bodySmall"
              color="#666666"
              whiteSpace="nowrap"
            >
              Or
            </Text>
            <Divider borderColor="#D0D5DD" />
          </Flex>
          <Box w="full">
            <SsoOptions setOauthPopup={setOauthPopup} />
          </Box>
        </VStack>
      </form>
    </>
  );
}

export default LoginPage;
